import type { FC } from "react";
import exporting from "highcharts/modules/exporting";
import exportData from "highcharts/modules/export-data"; // Import export-data module
import { useSearchParams } from "react-router-dom";
import * as HighCharts from "highcharts";
import HighChartsReact from "highcharts-react-official";
import { saveAs } from "file-saver";

import {
  MenuItem,
  Select,
  SelectChangeEvent,
  Box,
  Container,
  Stack,
  Paper,
  Divider,
  Modal,
} from "@mui/material";

import ButtonTabs, { ButtonTab } from "../../components/@common/ButtonTabs";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "../../components/@common/TableHead";
import TableBody from "../../components/@common/TableBody";
import TableRow from "@mui/material/TableRow";
import MuiTableCell from "@mui/material/TableCell";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import SvgIcon from "@mui/material/SvgIcon";
import DataFilters, { SelectedOptions } from "./DataFilters";
import { ReactComponent as DataPlaceholderIcon } from "../../@lib/icons/data-illustration-icon.svg";
import { ReactComponent as TableIcon } from "../../@lib/icons/table-icon.svg";
import { ReactComponent as ChartIcon } from "../../@lib/icons/chart-icon.svg";

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { styled } from "@mui/material/styles";
import { useLazyQuery, gql } from "@apollo/client";
import { useNotify } from "../../components/@common/NotificationsProvider";
import {
  ConditionalRenderer,
  useLanguageTranslator,
  Header,
} from "../../components";

import { chartTypes } from "./helpers";
import type { IDType, IndicatorValue } from "../../@lib/types";
import { QuantityChart } from "./quatity";

const TableCell = styled(MuiTableCell)({
  "&:nth-of-type(even)": {
    "&.MuiTableCell-head:before": {
      backgroundColor: "rgba(94, 113, 176, 0.1)",
    },
    "&.MuiTableCell-body": {
      backgroundColor: "rgba(94, 113, 176, 0.1)",
    },
  },
});

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 530,
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  py: 3,
  px: 1,
};

const indicatorValueQueryDocument = gql`
  query IndicatorValue(
    $indicatorIds: [ID!]!
    $countryIds: [ID!]!
    $startYear: Int!
    $endYear: Int!
  ) {
    indicatorValues(
      indicatorIds: $indicatorIds
      countryIds: $countryIds
      startYear: $startYear
      endYear: $endYear
    ) {
      year
      value
      description
      indicator {
        id
        label
        ratingType
      }
    }
  }
`;
// id

const Indicators: FC = () => {
  const notify = useNotify();
  const [currTab, setCurrTab] = useState("table");
  const [selectedOptions, setSelectedOptions] =
    useState<SelectedOptions | null>(null);
  const [checkedCountryList, setCheckedCountryList] = useState<any>([]);
  const [checkedThemeList, setCheckedThemeList] = useState([]);
  const [selectedCountryID, setSelectedCountryID] = useState<any>("");
  const [selectedTheme, setSelectedTheme] = useState<any>("");
  const [chartType, setChartType] = useState<any>("column");
  const [isOpen, setIsOpen] = useState({ description: null, status: false });
  const [selectedIndicators, setSelectedIndicators] = useState<
    Set<IDType> | null | any
  >(new Set<IDType>());
  const [radioSelectedCountry, setRadioSelectedCountry] = useState({
    label: "",
  });

  const { translator, currentLanguage } = useLanguageTranslator();
  const [searchParams] = useSearchParams();

  const [getIndicatorValues, { data, loading, refetch }] = useLazyQuery<{
    indicatorValues: IndicatorValue[];
  }>(indicatorValueQueryDocument, {
    fetchPolicy: "network-only",
    context: {
      headers: {
        "accept-language": currentLanguage || "en",
      },
    },
  });

  const isModule = Number(searchParams.getAll("by")?.[0] || "");

  const yearsList = useMemo(() => {
    if (!selectedOptions?.yearsRange) return [];

    const startYear = Math.min(
      selectedOptions.yearsRange.startYear,
      selectedOptions.yearsRange.endYear,
    );
    const endYear = Math.max(
      selectedOptions.yearsRange.startYear,
      selectedOptions.yearsRange.endYear,
      0,
    );
    return Array(endYear - startYear + 1)
      .fill(null)
      .map((_, i) => startYear + i);
  }, [selectedOptions]);

  const parsedPercentageData = useMemo(() => {
    if (!data) return data;

    const indicatorsMap = {} as {
      [key: string]: {
        type: string;
        label: string;
        data: Record<string | number, any>;
      };
    };

    data?.indicatorValues?.forEach((v: any) => {
      const map = indicatorsMap[v.indicator.id] || {
        type: v.indicator.ratingType,
        label: v.indicator.label,
        data: {},
        description: v?.description,
      };
      map.data[v.year] = v.value === "Oui" ? "0" : (v.value ?? "0");
      indicatorsMap[v.indicator.id] = map;
    });

    return Object.values(indicatorsMap);
  }, [data]);

  const percentageData =
    data?.indicatorValues?.filter(
      (item) => item?.indicator?.ratingType === "percentage",
    ) || [];

  const amountData =
    data?.indicatorValues?.filter(
      (item) => item?.indicator?.ratingType === "amount",
    ) || [];

  const quantityData =
    data?.indicatorValues?.filter(
      (item) => item?.indicator?.ratingType === "quantity",
    ) || [];

  /**
   * BEGIN: Chart options
   */

  if (typeof HighCharts === "object") {
    exporting(HighCharts);
    exportData(HighCharts);
  }

  function addWatermark(chart: any) {
    if (chart.watermark) {
      chart.watermark.destroy();
    }

    const watermarkX = chart.chartWidth / 2 - 150;
    const watermarkY = chart.chartHeight / 2 - 50;

    chart.watermark = chart.renderer
      .image(
        "https://niyel-dev.testaristarc.com/assets/images/watermark.png",
        watermarkX,
        watermarkY,
        400,
        100,
      )
      .attr({
        zIndex: 10,
        opacity: 0.5,
      })
      .add();
  }

  const chartOptions = useMemo(() => {
    if (!parsedPercentageData) return undefined;

    return {
      chart: {
        type: chartType,
        // plotBackgroundImage:
        //   "https://niyel-dev.testaristarc.com/assets/images/watermark.png",
        events: {
          load() {
            addWatermark(this);
          },
          redraw() {
            const yes: any = this;
            if (!yes.watermark) {
              addWatermark(this);
            }
          },
        },
      },
      tooltip: {
        enabled: true,
        shared: false,
        followPointer: true,
        useHTML: true,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        style: {
          color: "#fff",
        },
      },

      title: {
        text: null,
        align: "left",
      },
      xAxis: {
        type: "category",
      },
      exporting: {
        allowImageLoading: true,
        enabled: true,
        allowHTML: true,
        chartOptions: {
          chart: {
            events: {
              load() {
                const chart = this as any;
                addWatermark(chart);
              },
            },
          },
        },
        buttons: {
          contextButton: {
            menuItems: [
              "downloadPNG",
              "downloadJPEG",
              "downloadPDF",
              "downloadSVG",
              "downloadCSV",
            ],
          },
        },
        fallbackToExportServer: true,
      },
      navigation: {
        buttonOptions: {
          enabled: true,
        },
      },
      yAxis: {
        labels: {
          enabled: true,
        },
        title: {
          enabled: false,
        },
      },
      series: (() => {
        let tempSeries = [];
        for (let data of parsedPercentageData) {
          if (data.type !== "percentage") continue;

          tempSeries.push({
            // color: '#F9B500',
            borderRadius: 8,
            pointWidth: 32,
            name: data.label,
            label: {
              enabled: false,
            },
            data: Object.keys(data.data)
              .sort()
              .map((k) => ({ y: Number(data.data[k]) || 0, name: k })),
          });
        }
        return tempSeries;
      })(),
    };
  }, [parsedPercentageData, chartType]);
  /**
   * END: Chart options
   */
  const handleFiltersUpdate = useCallback((opts: SelectedOptions) => {
    setCheckedCountryList(opts?.selectedCountryList);
    setCheckedThemeList(opts?.selectedThemeList);
    setRadioSelectedCountry(opts?.countries);
    const optionsObj = {
      theme: opts?.theme,
      category: opts?.category,
      indicatorIds: opts?.indicatorIds,
      countries: [Array.from(opts?.countries?.value || "").join(",")],
      yearsRange: opts?.yearsRange,
    };
    setSelectedOptions(optionsObj);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (
          !selectedOptions ||
          // !selectedOptions.indicatorIds ||
          !selectedOptions.yearsRange?.startYear ||
          !selectedOptions.yearsRange.endYear
        )
          return;

        await getIndicatorValues({
          variables: {
            indicatorIds: selectedOptions.indicatorIds,
            countryIds: selectedOptions.countries,
            startYear: selectedOptions.yearsRange.startYear,
            endYear: selectedOptions.yearsRange.endYear,
          },
        });
      } catch (err) {
        notify.error(err as Error);
      }
    })();
  }, [
    getIndicatorValues,
    notify,
    selectedOptions,
    selectedCountryID,
    currentLanguage,
  ]);

  useEffect(() => {
    refetch();
  }, [currentLanguage]);

  const hasData = parsedPercentageData && parsedPercentageData.length > 0;

  const handleChange = (event: SelectChangeEvent) => {
    setSelectedCountryID(event?.target?.value);
  };

  const handleThemeChange = (event: SelectChangeEvent) => {
    setSelectedIndicators(null);
    setSelectedTheme(event?.target?.value);
  };

  function handleChartType(event: SelectChangeEvent) {
    setChartType(event?.target?.value);
  }

  const countryName: any =
    checkedCountryList?.find(
      (item: { value: string }) =>
        item.value === Array.from(selectedCountryID).join(",") || "",
    ) || {};

  useEffect(() => {
    if (checkedCountryList?.length >= 1) {
      const objOfCountry =
        checkedCountryList?.find((item: { value: string }) => item) || {};
      setSelectedCountryID(objOfCountry?.value);
      return;
    }
  }, [checkedCountryList]);

  const themeName: any =
    [...(checkedThemeList || [])]?.find(
      (item: { value: string }) =>
        item.value === Array.from(selectedTheme).join(",") || "",
    ) || {};

  useEffect(() => {
    if (checkedThemeList?.length >= 1) {
      const objOfTheme = [...(checkedThemeList || [])]?.find(
        (item: { value: string }) => item,
      ) || { value: "" };
      setSelectedTheme(objOfTheme?.value);
      return;
    }
  }, [checkedThemeList]);

  const updatedIndicatorsIds = selectedOptions?.indicatorIds
    ? selectedOptions?.indicatorIds?.length > 0
    : [];

  const countries =
    selectedOptions?.countries.length > 0 &&
    selectedOptions?.countries.every((item: any) => item === "");

  const isFieldFilled =
    Object?.keys(selectedOptions?.category || {})?.length > 0 &&
    !countries &&
    updatedIndicatorsIds &&
    Object?.keys(selectedOptions?.theme || {})?.length > 0 &&
    Object?.keys(selectedOptions?.yearsRange || {})?.length > 0;

  const updatedIndicatorValues = data?.indicatorValues || [];

  function handleIndicatorDetails(description: any) {
    setIsOpen({ description: description || "", status: true });
  }

  function handleClose() {
    setIsOpen({ description: null, status: false });
  }

  const downloadCSV = () => {
    if (!parsedPercentageData?.length) return;

    // Define CSV headers (Title + Years)
    const headers = ["Title", ...yearsList];

    // Convert table data to CSV format
    const csvRows = parsedPercentageData?.map((d) => {
      const row = [d?.label]; // First column: Title
      yearsList.forEach((y) => {
        row.push(
          d?.data[y]
            ? d?.data[y] + (d?.type === "percentage" ? "%" : "")
            : "--",
        );
      });
      return row.join(","); // Convert row array to CSV string
    });

    // Combine headers + rows
    const csvString = [headers.join(","), ...csvRows].join("\n");

    // Create a Blob and trigger download
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "indicators.csv");
  };

  const indicatorsData = parsedPercentageData || [];

  return (
    <Box pb={3}>
      <Header
        title={translator("Indicators")}
        subtitle=""
        isSearchField={false}
      />
      <Modal
        open={isOpen?.status}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography variant="h4" color="black" sx={{ fontSize: "16px" }}>
            {translator("dashboardTableHead.Description")}
          </Typography>{" "}
          <Typography
            variant="h4"
            sx={{ fontSize: "12px" }}
            color="#494E54"
            mt={1}
          >
            {isOpen?.description}
          </Typography>
          <Stack direction={"row"} justifyContent={"flex-end"}>
            <Button
              variant="contained"
              sx={{ borderRadius: "20px", p: "0px 40px", marginTop: "10px" }}
              onClick={handleClose}
            >
              {translator("generic.Close")}
            </Button>
          </Stack>
        </Box>
      </Modal>
      <Container sx={{ py: 3 }}>
        <Stack direction={{ lg: "row" }} gap={3}>
          <Paper
            sx={{
              flexBasis: { lg: "40%", xl: "30%" },
              py: 2,
              alignItems: { lg: "flex-start" },
              minWidth: 0,
            }}
          >
            <Container sx={{ pt: 2 }}>
              <Typography variant="h4" mb={3} lineHeight={1}>
                {translator("Please Select Data")}
              </Typography>
              <Divider sx={{ my: 1 }} />
            </Container>

            <DataFilters
              onUpdate={handleFiltersUpdate}
              setSelectedCountryID={setSelectedCountryID}
              selectedCountryID={selectedCountryID}
              isModule={isModule}
              selectedTheme={selectedTheme}
              setSelectedTheme={setSelectedTheme}
              setSelectedIndicators={setSelectedIndicators}
              selectedIndicators={selectedIndicators}
            />
          </Paper>

          <Paper component={Container} sx={{ flexGrow: 1, py: 2, minWidth: 0 }}>
            <Stack
              direction={{ md: "row" }}
              gap={1}
              justifyContent={{ md: "space-between" }}
            >
              <ButtonTabs
                value={currTab}
                onChange={(ev: any, v: any) => setCurrTab(v)}
              >
                <ButtonTab
                  label={translator("Table")}
                  value="table"
                  icon={
                    <SvgIcon
                      component={TableIcon}
                      inheritViewBox
                      fontSize="small"
                    />
                  }
                  iconPosition="start"
                />
                <ButtonTab
                  label={translator("Chart")}
                  value="chart"
                  icon={
                    <SvgIcon
                      component={ChartIcon}
                      inheritViewBox
                      fontSize="small"
                    />
                  }
                  iconPosition="start"
                />
              </ButtonTabs>
              <ConditionalRenderer condition={currTab === "table"}>
                <Button
                  sx={{ backgroundColor: "#00AAB0" }}
                  variant="contained"
                  onClick={downloadCSV}
                  disabled={
                    (isFieldFilled && updatedIndicatorValues?.length <= 0) ||
                    indicatorsData?.length <= 0
                  }
                >
                  {translator("Download")}
                </Button>
              </ConditionalRenderer>
            </Stack>
            <div style={{ display: "flex", width: "100%" }}>
              <ConditionalRenderer
                condition={checkedCountryList?.length > 1 && isModule === 1}
              >
                <div style={{ width: "35%" }}>
                  <Select
                    sx={{
                      border: "0px",
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                      outline: "0px",
                      margin: "10px 0px",
                      width: "100%",
                    }}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={Array.from(selectedCountryID).join(",")}
                    onChange={handleChange}
                    disabled={selectedCountryID === "" ? true : false}
                  >
                    {checkedCountryList?.map((elem: any) => (
                      <MenuItem value={elem?.value}>{elem?.label}</MenuItem>
                    ))}
                  </Select>
                </div>
              </ConditionalRenderer>
              <ConditionalRenderer
                condition={
                  [...(checkedThemeList || [])]?.length > 1 && isModule === 2
                }
              >
                <div style={{ width: "35%" }}>
                  <Select
                    sx={{
                      border: "0px",
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                      outline: "0px",
                      margin: "10px 0px",
                      width: "100%",
                    }}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={Array.from(selectedTheme).join(",")}
                    onChange={handleThemeChange}
                    disabled={selectedTheme === "" ? true : false}
                  >
                    {[...(checkedThemeList || [])]?.map((elem: any) => (
                      <MenuItem value={elem?.value}>{elem?.label}</MenuItem>
                    ))}
                  </Select>
                </div>
              </ConditionalRenderer>
              <ConditionalRenderer
                condition={(currTab === "chart" && hasData) || false}
              >
                <div style={{ width: "35%", marginLeft: "10px" }}>
                  <Select
                    sx={{
                      border: "0px",
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                      outline: "0px",
                      margin: "10px 0px",
                      width: "100%",
                    }}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={chartType}
                    onChange={handleChartType}
                  >
                    {chartTypes?.map((elem: any) => (
                      <MenuItem value={elem?.value}>{elem?.label}</MenuItem>
                    ))}
                  </Select>
                </div>
              </ConditionalRenderer>
            </div>
            <Divider sx={{ my: 2 }} />

            <Stack direction={{ md: "row" }} gap={3} flexWrap="nowrap">
              <div>
                <Typography
                  component="span"
                  variant="body2"
                  color="textSecondary"
                  mr={1}
                >
                  {translator("Theme")}:
                </Typography>
                <Typography component="span" variant="body2" fontWeight={500}>
                  {selectedOptions?.theme?.label || themeName?.label || "--"}
                </Typography>
              </div>
              <div>
                <Typography
                  component="span"
                  variant="body2"
                  color="textSecondary"
                  mr={1}
                >
                  {translator("dashboardTableHead.Category")}:
                </Typography>
                <Typography component="span" variant="body2" fontWeight={500}>
                  {selectedOptions?.category?.label || "--"}
                </Typography>
              </div>
              <div>
                <Typography
                  component="span"
                  variant="body2"
                  color="textSecondary"
                  mr={1}
                >
                  {translator("Country")}:
                </Typography>
                <Typography component="span" variant="body2" fontWeight={500}>
                  {countryName?.label || radioSelectedCountry?.label || "--"}
                </Typography>
              </div>
              <div>
                <Typography
                  component="span"
                  variant="body2"
                  color="textSecondary"
                  mr={1}
                >
                  {translator("Year")}:
                </Typography>
                <Typography
                  color={
                    isFieldFilled && updatedIndicatorValues?.length <= 0
                      ? "#FF6D6D"
                      : "black"
                  }
                  component="span"
                  variant="body2"
                  fontWeight={500}
                >
                  {selectedOptions?.yearsRange?.startYear} -{" "}
                  {selectedOptions?.yearsRange?.endYear}
                </Typography>
              </div>
            </Stack>

            <Box minHeight={600}>
              {!loading && !hasData && (
                <Stack
                  sx={{ height: 600 }}
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  <SvgIcon
                    component={DataPlaceholderIcon}
                    inheritViewBox
                    sx={{ fontSize: 100 }}
                  />
                  <Typography
                    textAlign="center"
                    color="textSecondary"
                    variant="body2"
                  >
                    <ConditionalRenderer condition={!isFieldFilled}>
                      {/* Please select a variable in each of the dimensions on the left to view the data. */}
                      {translator(
                        "Please select a variable in each of the dimensions on the left to view the data.",
                      )}
                    </ConditionalRenderer>
                    <ConditionalRenderer
                      condition={
                        isFieldFilled && updatedIndicatorValues?.length <= 0
                      }
                    >
                      {translator(
                        "No data is available for the selected time period. Please try choosing a different time range.",
                      )}
                    </ConditionalRenderer>
                    <br />
                  </Typography>
                </Stack>
              )}

              {loading && (
                <Stack
                  sx={{ height: 600 }}
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  <CircularProgress size={32} />
                  <Typography
                    textAlign="center"
                    color="textSecondary"
                    variant="body2"
                  >
                    {translator("Loading data")}...
                  </Typography>
                </Stack>
              )}

              {currTab === "table" && !loading && hasData && (
                <TableContainer>
                  <Table size="medium">
                    <TableHead>
                      <TableRow>
                        <TableCell>{translator("Title")}</TableCell>
                        {yearsList.map((y, i) => (
                          <TableCell key={`${i}-${y}`} align="center">
                            {y}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody sx={{ border: "1px solid #E4E7EC" }}>
                      {parsedPercentageData?.map((d: any, i: any) => (
                        <TableRow key={`${i}-${d.label}`} hover>
                          <TableCell
                            title={d.label || ""}
                            sx={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 300,
                              padding: "10px 20px",
                            }}
                          >
                            {d.label || ""}
                          </TableCell>
                          {yearsList.map((y, j) => (
                            <TableCell key={`${i}-${j}-${y}`} align="center">
                              <ConditionalRenderer condition={!d.data[y]}>
                                {"--"}
                              </ConditionalRenderer>
                              <ConditionalRenderer condition={d.data[y]}>
                                <small
                                  style={{
                                    textDecoration: "underline",
                                    color: "#00AAB0",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    handleIndicatorDetails(d?.description)
                                  }
                                >
                                  {d.data[y]}
                                  {d.data[y] &&
                                  d.type === "percentage" &&
                                  d.data[y] !== "-"
                                    ? "%"
                                    : ""}
                                </small>
                              </ConditionalRenderer>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {currTab === "chart" && !loading && hasData && (
                <Box mt={2}>
                  <Typography
                    sx={{
                      fontSize: "25px",
                      fontWeight: "800",
                      display: "flex",
                      justifyContent: "center",
                    }}
                    my={5}
                  >
                    Percentage Chart
                  </Typography>
                  {chartOptions && chartOptions.series.length > 0 ? (
                    <HighChartsReact
                      highcharts={HighCharts}
                      options={chartOptions}
                      containerProps={{
                        style: { height: 500, pointerEvents: "auto" },
                      }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: "25px",
                        fontWeight: "800",
                        display: "flex",
                        justifyContent: "center",
                      }}
                      my={5}
                    >
                      {translator("No data to plot")} Percentage Chart.
                    </Typography>
                  )}

                  {chartOptions && chartOptions.series.length > 0 ? (
                    <QuantityChart
                      type="quantity"
                      heading="Quantity Chart"
                      chartType={chartType}
                      data={quantityData}
                    />
                  ) : (
                    <Typography>{translator("No data to plot")}.</Typography>
                  )}
                  {chartOptions && chartOptions.series.length > 0 ? (
                    <QuantityChart
                      type="amount"
                      heading="Amount Chart"
                      chartType={chartType}
                      data={amountData}
                    />
                  ) : (
                    <Typography>{translator("No data to plot")}.</Typography>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
};

export { Indicators };
